import { createTheme, darken } from "@mui/material/styles";
import {
  BlueColors,
  customColors,
  GreenColors,
  OrangeColors,
  RedColors,
  SlateColors,
  VikingColors
} from "./colors";
import { designTokens, semanticTokens, SemanticTokens } from "./tokens";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    supportApp: true;
    f1: true;
    web: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    default: {
      main: string;
      contrastText: string;
    };
    slate: SlateColors;
    viking: VikingColors;
    red: RedColors;
    orange: OrangeColors;
    green: GreenColors;
    blue: BlueColors;
    ae: SemanticTokens;
  }

  interface PaletteOptions {
    default?: {
      main: string;
      light: string;
      contrastText: string;
    };
    slate?: SlateColors;
    viking?: VikingColors;
    red?: RedColors;
    orange?: OrangeColors;
    green?: GreenColors;
    blue?: BlueColors;
    ae?: SemanticTokens;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    default: true;
  }
}

const theme = createTheme({
  cssVariables: true,
  typography: {
    htmlFontSize: 16,
    fontFamily: designTokens.fontSans,
    fontSize: 14,

    h1: {
      fontSize: "1.25rem", // 28px
      lineHeight: "2rem", // 44px
      fontWeight: 700,
      color: customColors.slate.slate80,
      "@media (min-width:1024px)": {
        //desktop styles
        fontSize: "2rem",
        lineHeight: "2.75rem"
      }
    },
    h2: {
      fontSize: "1.75rem", // 24px
      lineHeight: "2rem", // 32px
      fontWeight: 700,
      color: customColors.slate.slate100
    },
    h3: {
      fontSize: "1.5rem", // 24px
      lineHeight: "2rem", // 32px
      fontWeight: 700,
      color: customColors.slate.slate100
    },
    h4: {
      fontSize: "1.25rem",
      lineHeight: "1.5rem",
      fontWeight: 700,
      color: customColors.slate.slate100
    },
    body1: {
      fontSize: "1rem", // 16px
      lineHeight: "1.5rem", // 24px
      fontWeight: 400,
      color: customColors.slate.slate80
    },
    body2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      fontWeight: 400,
      color: customColors.slate.slate60
    },
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      fontWeight: 400,
      color: customColors.slate.slate40
    }
  },

  palette: {
    primary: {
      main: customColors.viking.viking60,
      light: customColors.viking.viking70,
      contrastText: customColors.slate.slate00
    },
    secondary: {
      main: customColors.slate.slate60,
      light: customColors.slate.slate10,
      contrastText: customColors.slate.slate80
    },
    default: {
      main: customColors.slate.slate00,
      light: customColors.slate.slate10,
      contrastText: customColors.slate.slate80
    },
    background: {
      paper: customColors.slate.slate10
    },
    divider: customColors.slate.slate30,
    ...customColors,
    ae: semanticTokens
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shadows: Array(25).fill("none") as any,
  breakpoints: {
    values: {
      supportApp: 320,
      f1: 480,
      web: 1024
    } as const
  },
  shape: {
    borderRadius: 8
  },

  components: {
    MuiButton: {
      defaultProps: {
        color: "secondary"
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const { color } = ownerState;
          const colorKey =
            color && color !== "inherit" && color in theme.palette
              ? color
              : "default";

          const baseStyle = {
            fontWeight: 400,
            textTransform: "unset" as const
          };
          if (ownerState.variant === "text") {
            return {
              ...baseStyle,
              "&:hover, &:active": {
                color: darken(theme.palette[colorKey].main, 0.3),
                backgroundColor: "transparent"
              }
            };
          }
          if (ownerState.color === "default") {
            return {
              ...baseStyle,
              "&:hover, &:active": {
                backgroundColor: theme.palette.slate.slate10
              }
            };
          }

          return baseStyle;
        }
      }
    }
  }
});

export default theme;
